import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Collapse from "@material-ui/core/Collapse";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import { ReactNode, SyntheticEvent, useState } from "react";
import { changeAppState } from "../../../../common/appState";
import { useScreenSize } from "../../../../common/hooks/useScreenSize";
import { FetchResult, Ok } from "../../../../types/fetch";
import ShurikenProgress from "../../../shared/Animations/ShurikenProgress";
import CharacterComment from "../../../shared/CharacterComment";
import { TextField } from "../../../shared/Input/TextField";
import { signInPanelWidth } from "./Panel";
import { useOpenState } from "./SignUp/SignUp";

export function ForgotPassword({
    chosen,
    panelClosed,
    email,
    setEmail,
    setPassword,
}: {
    chosen: boolean;
    panelClosed: boolean;
    email: string;
    setEmail: (email: string) => void;
    setPassword: (password: string) => void;
}) {
    const c = useStyles();
    const { screenWidth } = useScreenSize();
    const [submissionError, setSubmissionError] = useState<ReactNode>(null);
    const [submitting, setSubmitting] = useState(false);
    const [mode, setMode] = useState<"sendEmail" | "setNewPassword">(
        "sendEmail"
    );

    const { partiallyOpened, containerC } = useOpenState(
        chosen,
        panelClosed,
        screenWidth > 600 ? 25 : 15
    );
    if (!partiallyOpened) {
        return null;
    }

    const sendEmail = async (ev: SyntheticEvent) => {
        ev.preventDefault();

        setSubmitting(true);
        const res = await fetch("api/Auth/SendEmailToChangePassword", {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
                email,
            }),
        });
        const result: FetchResult<Ok> = await res.json();

        if ("message" in result) {
            if (result.message === "success") {
                setMode("setNewPassword");
            } else {
                setSubmissionError("Failed to send an email");
            }
        } else {
            setSubmissionError(getEmailError(result.error));
        }
        setSubmitting(false);
    };

    return (
        <Container component="div" className={containerC.container}>
            <div className={c.paper}>
                <Avatar className={c.avatar}>
                    <LockOutlinedIcon />
                </Avatar>
                <h2 className={c.h2Title}>Forgot Password</h2>
                {submissionError && (
                    <div className={c.errorContainer}>{submissionError}</div>
                )}

                <Collapse in={mode === "sendEmail"} timeout={500}>
                    <form className={c.form} onSubmit={sendEmail}>
                        <div className={c.explanation}>
                            Enter your email address that you used to register.
                            Lingual Ninja will send you an email with a link to
                            reset your password.
                        </div>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            label="Email Address"
                            autoComplete="email"
                            onChange={ev => {
                                setEmail(ev.target.value);
                                setSubmissionError(null);
                            }}
                            value={email}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            className={c.submit}
                            disabled={submitting || !email}
                        >
                            {submitting ? (
                                <ShurikenProgress size="7%" />
                            ) : (
                                "Reset password"
                            )}
                        </Button>
                    </form>
                    <Grid container>
                        <Grid item xs>
                            <a
                                href="#"
                                onClick={ev => {
                                    ev.preventDefault();
                                    changeAppState("signInPanelState", {
                                        type: "signIn",
                                    });
                                }}
                            >
                                {"Remember your password?"}
                            </a>
                        </Grid>
                        <Grid item></Grid>
                    </Grid>
                </Collapse>

                <Collapse in={mode === "setNewPassword"} timeout={500}>
                    <CharacterComment
                        imgNumber={1}
                        screenWidth={Math.min(
                            signInPanelWidth - 58,
                            screenWidth
                        )}
                        comment={
                            <div className={c.charComment}>
                                An email was sent to{" "}
                                <span className="bold">{email}</span>! Please
                                check your email!
                            </div>
                        }
                        style={{ marginTop: 20 }}
                    />
                </Collapse>
            </div>
        </Container>
    );
}

function getEmailError(error: string): ReactNode {
    if (error === "emailNoMatch") {
        return (
            <span>
                Sorry, we can't find an account with this email address.
                <br />
                Please try again or{" "}
                <a
                    href="#"
                    onClick={ev => {
                        ev.preventDefault();
                        changeAppState("signInPanelState", { type: "signUp" });
                    }}
                >
                    create a new account
                </a>
                .
            </span>
        );
    }
    return error;
}

export const useStyles = makeStyles(theme => ({
    paper: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(4),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },
    form: {
        width: "100%",
        marginTop: theme.spacing(1),
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },
    errorContainer: {
        padding: 10,
        textAlign: "center",
        fontWeight: "bold",
        marginTop: 15,
        color: "red",
    },
    h2Title: { fontWeight: "bold", marginTop: 10 },
    explanation: { margin: "20px 0 15px" },
    charComment: {
        textAlign: "left",
        padding: "0 8px",
        fontSize: "large",
    },
}));
